// Notes editor — the closed-state LIST. Moved from index.html renderVault, MINUS the
// inline `if(isOpen){...}` editor body (which now lives in editor.js inside the overlay).
// Each row opens the overlay via S.activeVaultId + re() (onRender in index.js mounts it),
// instead of expanding inline. Markup/badges/order preserved so the list renders identically.
import { computeNoteChip, isDefaultNoteTitle, noteSortKey, noteDateLabel, noteSessionType } from './helpers.js';
import { notesCreateLocal, notesPendingIds } from './editor.js';
import { autoPickSession } from './picker.js';
const H = () => window.HUD || {};

// Short date "Sep 4" (matches the past-sessions rail); safe fallback on unparseable.
function _shortDate(ds){
  try{ const d=new Date(String(ds)+'T12:00:00'); if(isNaN(d)) return ds||'';
    return H()._locDate(d,{month:'short',day:'numeric'},'en-US'); }catch(e){ return ds||''; }
}

export function renderList(){
  const { el, mkBtn, T, DB, S, session, _supa } = H();
  const specId = session?.specialistId || null;
  const _ctx = H()._currentPatientContext();
  // Resolve patient display info from whichever model is active
  const child = _ctx?.kind==='child' ? DB.children.find(c=>c.id===S.activeChild) : null;
  const _sp = _ctx?.kind==='spec_patient' ? (DB.specialistPatients||[]).find(p=>p.id===S.activeSpecPatient) : null;
  const _patName = child?.name || _sp?.name || '';
  const _patAvatar = child?.avatar || _sp?.avatar_emoji || '🧒';
  // Filter notes by context
  // Notes SC2 A3: a not-yet-saved new note (local:true) is listed only while it is the open note; any other
  // local entry is a leftover (e.g. the page reloaded while it was open, untouched) → drop it.
  // SC4: a placeholder row for a device copy (bkGhost: a new note not yet created / deleted on the server) stays listed.
  // F-10: a new note an editor still owns (left with its first save in flight / waiting offline) is not a leftover.
  const _pending=new Set(notesPendingIds());
  if((DB.vaultNotes||[]).some(n=>n.local&&!n.bkGhost&&n.id!==S.activeVaultId&&!_pending.has(n.id))) DB.vaultNotes=DB.vaultNotes.filter(n=>!n.local||n.bkGhost||n.id===S.activeVaultId||_pending.has(n.id));
  const myNotes = _ctx?.kind==='spec_patient'
    ? [...DB.vaultNotes.filter(n=>n.specialistId===specId&&n.specialistPatientId===_ctx.specialist_patient_id)].sort((a,b)=>noteSortKey(b).localeCompare(noteSortKey(a)))
    : [...DB.vaultNotes.filter(n=>n.specialistId===specId&&n.childId===S.activeChild)].sort((a,b)=>noteSortKey(b).localeCompare(noteSortKey(a))); // SC5: session date, newest first

  const sec=el('div',{class:'section'});
  const _vBar=H().mkSpecBackBar(_patName);if(_vBar)sec.appendChild(_vBar);
  const hd=el('div',{class:'sec-hd'});
  hd.appendChild(el('div',{class:'sec-hd-left'},[
    el('h2',{class:'page-title'},[T('vault_title')]),
    el('p',{class:'page-sub'},[T('vault_private_notes')+(_patName?(' · '+_patAvatar+' '+_patName):'')])
  ]));
  const _vaultRemoved=(child?.connectionStatus||'approved')==='removed';
  hd.appendChild(el('div',{class:'sec-hd-right'},[
    mkBtn(T('vault_new_note'),'btn-md btn-primary',async()=>{
      // SC5: the shared local-note factory (no row until the first autosave with text — QA-24). L3: auto-pick today's
      // session (in progress → ended most recently → later today → general), from memory — no request.
      const _pt={ childId:_ctx?.kind==='child'?_ctx.child_id:null, specialistPatientId:_ctx?.kind==='spec_patient'?_ctx.specialist_patient_id:null };
      const nn=notesCreateLocal({ ..._pt, appointment:autoPickSession(_pt) });
      S.activeVaultId=nn.id;S.vaultSummary=null;S.vaultHasUnsaved=false;H().re(); // SC1c: null = untouched
    })
  ]));
  sec.appendChild(hd);
  if(_vaultRemoved){const _vLock=el('div',{style:{background:'#fef3c7',border:'1.5px solid #f59e0b',borderRadius:'var(--r-lg)',padding:'10px 14px',marginBottom:'14px',display:'flex',alignItems:'center',gap:'8px',color:'#92400e'}});_vLock.appendChild(el('span',{style:{fontSize:'1rem'}},['🔌']));_vLock.appendChild(el('div',{style:{fontSize:'.8rem',fontWeight:600}},[T('vault_not_connected')]));sec.appendChild(_vLock);}

  if(!myNotes.length){
    sec.appendChild(el('div',{class:'vn2-empty'},[
      el('span',{class:'vn2-empty-icon','aria-hidden':'true'},['📋']),
      el('div',{class:'vn2-empty-title'},[T('vault_no_notes')]),
      el('div',{class:'vn2-empty-body'},[T('vault_no_notes_desc')]),
    ]));
    return sec;
  }

  // Note list — each row (whole card) opens the overlay editor. Newest first (unchanged).
  const noteStack=el('div',{class:'vn2-list'});

  myNotes.forEach(note=>{
    // role=button div (not <button>): a real button's intrinsic sizing breaks flex min-width:0
    // ellipsis and the phone column stack. Keep keyboard access via a keydown handler.
    const row=el('div',{class:'vn2-lr'+(S.activeVaultId===note.id?' vn2-lr-open':''),role:'button',tabindex:'0'});

    const head=el('div',{class:'vn2-lr-head'});
    const ch=computeNoteChip(note,{isOpenNote:false,withTime:false}); // state chip, no time
    head.appendChild(el('span',{class:'vn2-lr-chip',style:{background:ch.bg,color:ch.fg}},[ch.label]));
    const main=el('div',{class:'vn2-lr-main'});
    // SC5 L2 (queue item 5): the date comes from the data (session date + time, else the written date), never
    // the stored title; the session type from the appointment; a custom title as a second line.
    const _typ=noteSessionType(note);
    main.appendChild(el('span',{class:'vn2-lr-title'},[el('bdi',{dir:'auto'},[noteDateLabel(note)]), ...(_typ?[' · ', el('bdi',{dir:'auto'},[_typ])]:[])])); // F-27: the type is its own bidi part too
    if(!isDefaultNoteTitle(note)) main.appendChild(el('span',{class:'vn2-lr-custom'},[note.title]));
    head.appendChild(main);
    // Read-by-parent — only meaningful on a published note the parent has opened. Icon + tooltip on
    // desktop; a small text label (shown by CSS on <768) since touch has no hover.
    if(note.published && note.parentReadAt){
      const read=el('span',{class:'vn2-lr-read','title':T('vn2_read_by_parent'),'aria-label':T('vn2_read_by_parent')});
      read.appendChild(el('span',{class:'vn2-lr-check','aria-hidden':'true'},['✓']));
      read.appendChild(el('span',{class:'vn2-lr-readlbl'},[T('vn2_read_by_parent')]));
      head.appendChild(read);
    }
    row.appendChild(head);

    // One-line SUMMARY preview when present (never clinical content — content isn't even loaded).
    const _sum=(note.summary||'').trim();
    if(_sum) row.appendChild(el('div',{class:'vn2-lr-preview',dir:'auto'},[_sum])); // F-55c

    // Overlay model: the list is only visible when no note is open, so a row click always
    // opens that note. The unsaved-switch confirm from the old inline accordion is unreachable
    // (the overlay covers the list) — Back owns the unsaved confirm.
    const _open=()=>{ S.activeVaultId=note.id; S.vaultSummary=null; H().re(); }; // SC1c: null = untouched → the note's stored summary
    row.onclick=_open;
    row.onkeydown=(e)=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); _open(); } };
    noteStack.appendChild(row);
  });

  sec.appendChild(noteStack);
  return sec;
}
